import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Routes } from '~/domains/common/constants/routes';

import { getAppsFlyerLink } from '../appsflyer/appsflyer.service.api';
import type { APIAppsFlyerLinkResponse } from '../appsflyer/types';

import { DeepLinkType } from './types';
import { getQRcodeBase64 } from './nextApi.service';

// Private hook to get the deep link URL and optional QR code
const useGetDeepLinkURL = ({
  deepLinkType,
  contestId,
  referralCode,
  generateQRCode,
}: {
  deepLinkType: DeepLinkType,
  contestId: string,
  referralCode: string,
  generateQRCode: boolean,
}) => {
  const [appLink, setAppLink] = useState<string>(null);
  const [appLinkQR, setAppLinkQR] = useState<string>(null);

  const originalUrl = contestId
    ? Routes.contestDetail(contestId)
    : Routes.signUp({ referralCode });

  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data: linkResponse } = useQuery<APIAppsFlyerLinkResponse>({
    queryKey: [`deepLink/${deepLinkType}/url`, originalUrl],
    queryFn: () => getAppsFlyerLink(originalUrl, deepLinkType),
  });

  const deepLinkURL = useMemo(() => {
    // Fallback to original URL if linkResponse fails
    if (!linkResponse) return `${window.location.origin}${originalUrl}`;
    return linkResponse as string;
  }, [originalUrl, linkResponse]);

  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data: deepLinkQRBase64 } = useQuery({
    queryKey: [`deepLink/${deepLinkType}/qr-code`, deepLinkURL],
    enabled: !!deepLinkURL && generateQRCode,
    queryFn: () => getQRcodeBase64(deepLinkURL),
  });

  useEffect(() => {
    if (deepLinkURL) setAppLink(deepLinkURL);
    if (deepLinkQRBase64) setAppLinkQR(deepLinkQRBase64);
  }, [deepLinkURL, deepLinkQRBase64]);

  return [appLink, appLinkQR];
};

export const useContestDeepLinkWebToApp = ({
  contestId,
  deepLinkType,
}: {
  contestId: string;
  deepLinkType?: DeepLinkType;
}): [string, string | undefined] | null => {
  const [appLink, appLinkQR] = useGetDeepLinkURL({
    deepLinkType: deepLinkType || DeepLinkType.CONTEST_DETAIL,
    contestId,
    referralCode: null,
    generateQRCode: true,
  });

  return [appLink, appLinkQR];
};

export const useSignupDeepLinkWebToApp = (referralCode?: string, deepLinkType?: DeepLinkType) => {
  const [appLink] = useGetDeepLinkURL({
    deepLinkType: deepLinkType || DeepLinkType.SIGN_UP,
    contestId: null,
    referralCode,
    generateQRCode: false,
  });

  return [appLink];
};
